import type { ReviewQueueTask } from "@/api/tasks";
import type { PresentationState } from "@/lib/focus/focus-types";
import type { FrameData } from "@/lib/frame-data";
import { frameTypeLabel } from "@/components/shapes/shared/frame-type-label";

export const RULER_NEUTRAL_COLOR =
  "var(--denker-ruler-tick-neutral, rgb(113 113 122))";

export interface TickEntry {
  nodeId: string;
  title: string;
  color: string;
  taskTicket: FrameData["taskTicket"];
  groupKey: string;
}

export interface TickGroup {
  key: string;
  ticks: TickEntry[];
}

function titleForFrame(frame: FrameData): string {
  const title = frame.title.trim();
  return title || frameTypeLabel(frame.frameType);
}

/** A tick's colour for the agent (template id) that made its frame. */
export type AgentColor = (agentId: string) => string | null;

export interface TickGroupOptions {
  /**
   * `ruler`: pinned, per task, then unlinked, each by creation.
   * `given`: one group, in the caller's order.
   */
  order?: "ruler" | "given";
}

export const NEVER_HIDDEN = () => false;

/** The ruler's colours: the cursor colour of the agent that made the frame. */
export function agentCursorColor(
  agentCursors: Map<string, { agentId: string; color: string }>,
): AgentColor {
  return (agentId) => {
    for (const cursor of agentCursors.values()) {
      if (cursor.agentId === agentId) return cursor.color;
    }
    return null;
  };
}

function tickGroupKey(frame: FrameData): string {
  if (frame.metadata?.pinned === true) return "top";
  return frame.taskTicket ? `task-${frame.taskTicket.id}` : "unlinked";
}

/** Ticks for the frames that are not hidden, grouped by `options.order`. */
export function buildTickGroups(
  frames: readonly FrameData[],
  isHidden: (frameId: string) => boolean,
  agentColor: AgentColor,
  { order = "ruler" }: TickGroupOptions = {},
): TickGroup[] {
  const visible = frames.filter((frame) => !isHidden(frame.frameId));
  if (visible.length === 0) return [];

  const toTick = (frame: FrameData): TickEntry => ({
    nodeId: frame.frameId,
    title: titleForFrame(frame),
    color:
      (frame.agentId ? agentColor(frame.agentId) : null) ?? RULER_NEUTRAL_COLOR,
    taskTicket: frame.taskTicket,
    groupKey: tickGroupKey(frame),
  });

  if (order === "given") return [{ key: "given", ticks: visible.map(toTick) }];

  const top: FrameData[] = [];
  const taskMap = new Map<string, FrameData[]>();
  const unlinked: FrameData[] = [];

  for (const frame of visible) {
    const isPinned = frame.metadata?.pinned === true;
    if (isPinned) {
      top.push(frame);
    } else if (frame.taskTicket) {
      const taskId = frame.taskTicket.id;
      const group = taskMap.get(taskId) ?? [];
      group.push(frame);
      taskMap.set(taskId, group);
    } else {
      unlinked.push(frame);
    }
  }

  const sortByCreated = (left: FrameData, right: FrameData) =>
    left.createdAt.localeCompare(right.createdAt);

  top.sort(sortByCreated);
  unlinked.sort(sortByCreated);
  for (const group of taskMap.values()) group.sort(sortByCreated);

  const sortedTaskIds = [...taskMap.entries()]
    .sort(([, left], [, right]) => sortByCreated(left[0]!, right[0]!))
    .map(([taskId]) => taskId);

  const groups: TickGroup[] = [];
  if (top.length > 0) groups.push({ key: "top", ticks: top.map(toTick) });
  for (const taskId of sortedTaskIds) {
    groups.push({
      key: `task-${taskId}`,
      ticks: taskMap.get(taskId)!.map(toTick),
    });
  }
  if (unlinked.length > 0) {
    groups.push({ key: "unlinked", ticks: unlinked.map(toTick) });
  }
  return groups;
}

/** Every frame, hidden ones included, in ruler order. */
export function getRulerOrderedFrameIds(
  frames: readonly FrameData[],
): string[] {
  return buildTickGroups(frames, NEVER_HIDDEN, () => null).flatMap((group) =>
    group.ticks.map((tick) => tick.nodeId),
  );
}

export function buildPresentationGroups(
  presentation: PresentationState,
  frames: readonly FrameData[],
  agentColor: AgentColor,
  reviewTasks: ReviewQueueTask[],
): TickGroup[] {
  const frameTaskIds = new Map<string, string>();
  if (presentation.variant === "review") {
    for (const task of reviewTasks) {
      for (const frameId of task.frame_ids) {
        if (!frameTaskIds.has(frameId)) {
          frameTaskIds.set(frameId, task.task_id);
        }
      }
    }
  }

  const framesById = new Map(frames.map((frame) => [frame.frameId, frame]));
  const groups: TickGroup[] = [];
  let lastGroupKey: string | null = null;
  for (const frameId of presentation.frameIds) {
    const frame = framesById.get(frameId);
    if (!frame) continue;
    const taskId = frameTaskIds.get(frameId);
    const groupKey = taskId ? `task-${taskId}` : "presentation";
    const tick: TickEntry = {
      nodeId: frameId,
      title: titleForFrame(frame),
      color: RULER_NEUTRAL_COLOR,
      taskTicket: frame.taskTicket,
      groupKey,
    };
    if (groupKey === lastGroupKey) {
      groups[groups.length - 1]!.ticks.push(tick);
    } else {
      groups.push({ key: `${groupKey}:${groups.length}`, ticks: [tick] });
      lastGroupKey = groupKey;
    }
  }
  if (presentation.variant === "focus") {
    const focusedIds = new Set(presentation.frameIds);
    const availableGroups = buildTickGroups(
      frames.filter((frame) => !focusedIds.has(frame.frameId)),
      NEVER_HIDDEN,
      agentColor,
    );
    for (const group of availableGroups) {
      groups.push({ key: `available-${group.key}`, ticks: group.ticks });
    }
  }
  return groups;
}

/** Frames equal in every field a tick or the hidden rule reads. */
function sameTickFrame(left: FrameData, right: FrameData): boolean {
  return (
    left === right ||
    (left.frameId === right.frameId &&
      left.title === right.title &&
      left.frameType === right.frameType &&
      left.agentId === right.agentId &&
      left.createdAt === right.createdAt &&
      left.metadata?.pinned === right.metadata?.pinned &&
      left.metadata?.hidden === right.metadata?.hidden &&
      left.taskTicket?.id === right.taskTicket?.id &&
      left.taskTicket?.number === right.taskTicket?.number &&
      left.taskTicket?.title === right.taskTicket?.title)
  );
}

/** A content edit or a status change keeps the previous tick frames. */
export function tickFramesEqual(
  left: readonly FrameData[],
  right: readonly FrameData[],
): boolean {
  return (
    left.length === right.length &&
    left.every((frame, index) => sameTickFrame(frame, right[index]!))
  );
}

function ticksEqual(left: TickEntry, right: TickEntry): boolean {
  return (
    left.nodeId === right.nodeId &&
    left.title === right.title &&
    left.color === right.color &&
    left.groupKey === right.groupKey &&
    left.taskTicket?.id === right.taskTicket?.id &&
    left.taskTicket?.number === right.taskTicket?.number &&
    left.taskTicket?.title === right.taskTicket?.title
  );
}

/** Structural equality: a frame move rebuilds equal groups, not a new strip. */
export function tickGroupsEqual(
  left: TickGroup[],
  right: TickGroup[],
): boolean {
  return (
    left.length === right.length &&
    left.every((group, groupIndex) => {
      const other = right[groupIndex]!;
      return (
        group.key === other.key &&
        group.ticks.length === other.ticks.length &&
        group.ticks.every((tick, tickIndex) =>
          ticksEqual(tick, other.ticks[tickIndex]!),
        )
      );
    })
  );
}
